import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import PricingPage from './pages/PricingPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import DisclaimerPage from './pages/DisclaimerPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import { Loader2 } from 'lucide-react';
import './App.css';

// Lazy-loaded heavy pages (Mapbox GL, SendLetterSlideOut, etc.)
const MapPage = lazy(() => import('./pages/MapPage'));
const SearchListPage = lazy(() => import('./pages/SearchListPage'));
const ApplicationDetailPage = lazy(() => import('./pages/ApplicationDetailPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const IntelligencePage = lazy(() => import('./pages/IntelligencePage'));
const ScraperDashboard = lazy(() => import('./pages/ScraperDashboard'));
const CouncilIndexPage = lazy(() => import('./pages/CouncilIndexPage'));
const CouncilPage = lazy(() => import('./pages/CouncilPage'));
const CouncilMapPage = lazy(() => import('./pages/CouncilMapPage'));
const GuideIndexPage = lazy(() => import('./pages/GuideIndexPage'));
const GuidePage = lazy(() => import('./pages/GuidePage'));
const LettersPage = lazy(() => import('./pages/LettersPage'));
const AdminUsersPage = lazy(() => import('./pages/AdminUsersPage'));

function PageLoader() {
  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center">
      <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#00a896]" />
      </div>
    );
  }
  if (!user || user === false) {
    const currentPath = window.location.pathname + window.location.search;
    return <Navigate to={`/login?from=${encodeURIComponent(currentPath)}`} replace />;
  }
  return children;
}

const FULLSCREEN_ROUTES = ['/map'];

function AppLayout({ children }) {
  const location = useLocation();
  const isFullscreen = FULLSCREEN_ROUTES.some(r => location.pathname.startsWith(r));
  return (
    <>
      <Navbar />
      {children}
      {!isFullscreen && <Footer />}
    </>
  );
}

function AppRoutes() {
  return (
    <AppLayout>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/search" element={<SearchListPage />} />
          <Route path="/search/:location" element={<SearchListPage />} />
          <Route path="/search/:location/:type" element={<SearchListPage />} />
          <Route path="/search/:location/:type/:status" element={<SearchListPage />} />
          <Route path="/map" element={<MapPage />} />
          <Route path="/map/:location" element={<MapPage />} />
          <Route path="/map/:location/:type" element={<MapPage />} />
          <Route path="/map/:location/:type/:status" element={<MapPage />} />
          <Route path="/application/:id" element={<ApplicationDetailPage />} />
          <Route path="/planning-applications" element={<CouncilIndexPage />} />
          <Route path="/planning-applications/:council" element={<CouncilPage />} />
          <Route path="/planning-applications/:council/map" element={<CouncilMapPage />} />
          <Route path="/planning-permission" element={<GuideIndexPage />} />
          <Route path="/planning-permission/:type" element={<GuidePage />} />
          <Route path="/guide/:type" element={<GuidePage />} />
          <Route path="/letters" element={<LettersPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/intelligence" element={<IntelligencePage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/disclaimer" element={<DisclaimerPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/admin/scrapers" element={<ProtectedRoute><ScraperDashboard /></ProtectedRoute>} />
          <Route path="/p-admin" element={<ProtectedRoute><AdminUsersPage /></ProtectedRoute>} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </AppLayout>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
